/**
 * Guarantees `<mux-video>.play()` always returns a promise.
 *
 * custom-media-element forwards `play()` to the inner native video as
 * `nativeFn?.apply(this.nativeEl, args)`, which returns `undefined` instead of
 * throwing when `nativeEl` is momentarily null. playback-core's muted-autoplay
 * path then calls `.catch()` on that — Sentry OLLIEWEB-JMW. Both halves are
 * still present in the latest published packages, so there is nothing to
 * upgrade to.
 *
 * Deliberately not `'use client'`: `MuxVideo/index.js` is reached from the RSC
 * graph through the `@web/components` barrel, and a client export called from
 * the server is a reference stub that throws. The `window` check below is what
 * makes the server pass a no-op.
 */

const GUARDED = Symbol.for('ollie.muxPlayGuard');

export const guardMuxPlay = async (elementName = 'mux-video') => {
  if (typeof window === 'undefined' || !window.customElements) return;

  // Must wait for definition: custom-media-element installs `play` while
  // `customElements.define()` reads `observedAttributes`, and skips any prop
  // already on the prototype. Patching earlier stops the real forwarder from
  // ever being installed and breaks playback outright.
  await window.customElements.whenDefined(elementName);

  const { prototype } = window.customElements.get(elementName);
  const originalPlay = prototype.play;

  if (typeof originalPlay !== 'function' || originalPlay[GUARDED]) return;

  function play(...args) {
    return originalPlay.apply(this, args) ?? Promise.resolve();
  }
  play[GUARDED] = true;

  prototype.play = play;
};
